import { useCallback, useEffect } from 'react';
import type { KeyboardEvent } from 'react';
import loadable from '@loadable/component';

import { GridCellKind, getMiddleCenterBias } from '@glideapps/glide-data-grid';
import { CalendarDate } from '@internationalized/date';

import type { DateValue } from '@react-aria/calendar';

import type { CustomRenderer } from '@glideapps/glide-data-grid';

import type { CustomCellEditor, CustomCellProps as Props } from '../types';

import { drawCellError, isCustomCellOfKind } from '../utils';

import type { DateCell } from './types';

import { kind } from './types';

import {
  validatePastedValue,
  validateDate,
  getFomattedDateString,
} from './utils';

const DateInput = loadable(
  () => import('../../../../../../../components/DateInput'),
  {
    resolveComponent: (c) => c.DateInput,
  }
);

const Editor: CustomCellEditor<DateCell> = ({
  value: cell,
  onChange,
  onFinishedEditing,
}: Props<DateCell>) => {
  const handleChange = useCallback(
    (value: DateValue) => {
      if (value === null) return;

      const date = new Date();
      date.setFullYear(value.year, value.month - 1, value.day);

      const error = validateDate(date);

      onChange({
        ...cell,
        data: {
          ...cell.data,
          value: date,
          error,
        },
      });
    },
    [onChange, cell]
  );

  const handleBlur = useCallback(() => {
    if (cell.data.error === null) return onFinishedEditing(cell, [1, 0]);

    // Clear the value if there is an error
    onFinishedEditing(
      {
        ...cell,
        data: {
          ...cell.data,
          value: null,
        },
      },
      [1, 0]
    );
  }, [onFinishedEditing, cell]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Enter') handleBlur();
    },
    [handleBlur]
  );

  useEffect(() => {
    if (RUNTIME_ENV === 'native') {
      async function setAccessoryBar() {
        const { Device } = await import('@capacitor/device');
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'ios') {
          const { Keyboard } = await import('@capacitor/keyboard');
          Keyboard.setAccessoryBarVisible({ isVisible: true });
        }
      }

      async function unsetAccessoryBar() {
        const { Device } = await import('@capacitor/device');
        const deviceInfo = await Device.getInfo();
        if (deviceInfo.platform === 'ios') {
          const { Keyboard } = await import('@capacitor/keyboard');
          Keyboard.setAccessoryBarVisible({ isVisible: false });
        }
      }
      setAccessoryBar();
      return () => {
        unsetAccessoryBar();
      };
    }
  }, []);

  const date = cell.data.value ?? new Date();

  const value = cell.data.value
    ? new CalendarDate(date.getFullYear(), date.getMonth() + 1, date.getDate())
    : undefined;

  return (
    <div className="grid h-[53px] items-center px-x0_75 pl-x1_75">
      <DateInput
        aria-label="Transaction Date"
        autoFocus={true}
        value={value}
        lang={cell.data.lang}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onChange={handleChange}
      />
    </div>
  );
};

export const Render: CustomRenderer<DateCell> = {
  kind: GridCellKind.Custom,
  isMatch: isCustomCellOfKind(kind),
  draw: (args, cell) => {
    const { ctx, theme, rect } = args;
    const { value, error, lang } = cell.data;

    const isError = error !== null;

    if (isError) {
      drawCellError({ ctx, theme, rect, error });
    } else {
      ctx.fillStyle = theme.textDark;
      ctx.fillText(
        // Since getFomattedDateString returns a string in the format 'YYYY-MM-DD' via Intl.DateTimeFormat "en-CA"
        // Replace '-' with '/' to satify the products UX date formatting requirements
        getFomattedDateString(value, lang).replace(/-/g, '/'),
        rect.x + theme.cellHorizontalPadding,
        rect.y + rect.height / 2 + getMiddleCenterBias(ctx, theme)
      );
    }

    return true;
  },
  measure: (ctx, cell, theme) => {
    const { value, lang } = cell.data;
    const text = getFomattedDateString(value, lang);
    return ctx.measureText(text).width + theme.cellHorizontalPadding * 2;
  },
  provideEditor: () => ({
    editor: Editor,
    disablePadding: true,
  }),
  onPaste: validatePastedValue,
};
